import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, ExternalLink } from 'lucide-react';
import { companyInfo, services, cities } from '../../data/mock';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#262626] text-white">
      {/* CTA Banner - ROUGE */}
      <div className="bg-[#B91C1C]">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="text-xl md:text-2xl font-bold">Besoin d'un dépannage urgent à Vitry-sur-Seine ?</h3>
              <p className="text-white/80">Intervention en 30 minutes, 24h/24 et 7j/7</p>
            </div>
            <a 
              href={`tel:${companyInfo.phoneEmergency.replace(/\s/g, '')}`}
              className="flex items-center gap-3 bg-white hover:bg-gray-100 text-[#B91C1C] px-6 py-3 rounded-lg font-bold text-lg transition-colors"
            >
              <Phone className="w-6 h-6" />
              {companyInfo.phoneEmergency}
            </a>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info with Logo */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <img 
                src={companyInfo.logo} 
                alt="EMS Express Logo"
                className="h-12 w-auto object-contain"
              />
              <div>
                <h4 className="font-bold">EMS Express</h4>
                <p className="text-xs text-gray-400">Miroiterie • Serrurerie</p>
              </div>
            </div>
            <p className="text-gray-400 text-sm mb-4">
              Artisan vitrier et serrurier depuis plus de 20 ans. 
              Intervention rapide sur Vitry-sur-Seine et tout le Val-de-Marne (94).
            </p>
            <div className="space-y-2 text-sm">
              <a 
                href={`tel:${companyInfo.phoneEmergency.replace(/\s/g, '')}`}
                className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors"
              >
                <Phone className="w-4 h-4 text-[#B91C1C]" />
                {companyInfo.phoneEmergency}
              </a>
              <a 
                href={`mailto:${companyInfo.email}`}
                className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors"
              >
                <Mail className="w-4 h-4 text-[#B91C1C]" />
                {companyInfo.email}
              </a>
              <div className="flex items-start gap-2 text-gray-300">
                <MapPin className="w-4 h-4 text-[#B91C1C] mt-0.5" />
                <span>{companyInfo.fullAddress}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-300">
                <Clock className="w-4 h-4 text-[#B91C1C]" />
                <span>Urgence 24h/24 - 7j/7</span>
              </div>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-bold mb-4 text-lg">Nos Services</h4>
            <ul className="space-y-2 text-sm">
              {services.map((service) => (
                <li key={service.id}>
                  <Link 
                    to={`/services/${service.id}`}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    {service.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Zones - VITRY EN PREMIER */}
          <div>
            <h4 className="font-bold mb-4 text-lg">Zones d'intervention</h4>
            <ul className="space-y-1 text-sm grid grid-cols-2 gap-x-2">
              {cities.slice(0, 10).map((city) => (
                <li key={city.id}>
                  <Link 
                    to={`/zones/${city.id}`}
                    className={`transition-colors ${
                      city.isPrimary 
                        ? 'text-[#B91C1C] font-semibold hover:text-white' 
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    {city.name}
                  </Link>
                </li>
              ))}
            </ul>
            <Link 
              to="/zones" 
              className="inline-flex items-center gap-1 text-[#B91C1C] hover:text-[#DC2626] mt-2 text-sm"
            >
              Voir toutes les villes
              <ExternalLink className="w-3 h-3" />
            </Link>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-bold mb-4 text-lg">Liens rapides</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/" className="text-gray-400 hover:text-white transition-colors">
                  Accueil
                </Link>
              </li>
              <li>
                <Link to="/services" className="text-gray-400 hover:text-white transition-colors">
                  Nos services
                </Link>
              </li>
              <li>
                <Link to="/a-propos" className="text-gray-400 hover:text-white transition-colors">
                  À propos
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-400 hover:text-white transition-colors">
                  Demander un devis
                </Link>
              </li>
              <li>
                <Link to="/mentions-legales" className="text-gray-400 hover:text-white transition-colors">
                  Mentions légales
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar - Glotech */}
      <div className="border-t border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-2 text-sm text-gray-400">
            <p>© {currentYear} EMS Express Miroiterie Serrurerie - Vitry-sur-Seine. Tous droits réservés.</p>
            <div className="flex items-center gap-4">
              <Link to="/mentions-legales" className="hover:text-white transition-colors">
                Mentions légales
              </Link>
              <span>•</span>
              <a 
                href="https://www.info-glotech.fr" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:text-white transition-colors"
              >
                Fabriqué par Glotech
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;